import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { AccessNotice } from "../components/shared/AccessEndsBanner";

interface CurrentUser {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  is_super_admin: boolean;
  is_portal_admin: boolean;
  accessible_clients: string[];
  // their own end dates still to come (FORGE-408 banner); [] for staff
  access_notices?: AccessNotice[];
}

interface CurrentUserContextValue {
  currentUser: CurrentUser | null;
  loading: boolean;
  // Why the caller's permissions could not be confirmed (null = they were).
  // Without it a failed /me looked exactly like "not a super admin": controls
  // greyed out with "Only super-admins can…" (Mark, 2026-10-19).
  error: string | null;
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
  currentUser: null,
  loading: true,
  error: null,
});

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetches directly instead of via api.ts to avoid 401→login redirect loops
    // during initial auth negotiation. A timeout, a network error or a gateway
    // error (502/503/504) is tried once more with a longer wait: right after a
    // backend deploy the Function App's first answers can take over 10 s, and
    // locking the admin controls for that was the 2026-10-28 "Degraded" banner.
    // A real answer (200, 401, 403, 500) is never retried.
    const WAITS_MS = [10_000, 25_000];
    let cancelled = false;
    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (n: number): Promise<void> => {
      controller = new AbortController();
      let timedOut = false;
      timer = setTimeout(() => {
        timedOut = true;
        controller?.abort();
      }, WAITS_MS[n]);
      let retryable: string | null = null;
      try {
        const r = await fetch(`${API_BASE}/api/manage/users/me`, {
          signal: controller.signal,
          credentials: "include",
        });
        if (cancelled) return;
        if (r.ok) {
          setCurrentUser(await r.json());
          setError(null);
          return;
        }
        if ([502, 503, 504].includes(r.status)) {
          retryable = `HTTP ${r.status}: the server is starting up`;
        } else {
          // the server says why: 401 not signed in, 403 no active user record
          // for the signed-in email, 500 the lookup failed
          let why = r.statusText;
          try {
            why = (await r.json())?.error || why;
          } catch {
            // not JSON; keep the status text
          }
          setCurrentUser(null);
          setError(`HTTP ${r.status}${why ? `: ${why}` : ""}`);
          return;
        }
      } catch {
        if (cancelled) return; // unmounted
        retryable = timedOut
          ? `no answer within ${WAITS_MS[n] / 1000} seconds`
          : "the server could not be reached";
      } finally {
        clearTimeout(timer);
      }
      if (n + 1 < WAITS_MS.length) return attempt(n + 1);
      setCurrentUser(null);
      setError(retryable);
    };

    attempt(0).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
    };
  }, []);

  return (
    <CurrentUserContext.Provider value={{ currentUser, loading, error }}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser() {
  return useContext(CurrentUserContext);
}

/** The banner text when the caller's permissions could not be confirmed. */
export const permissionsUnconfirmed = (error: string) =>
  `Couldn't confirm your permissions (${error}), so admin controls are locked. Reload to try again.`;
